import { z } from 'zod';
import { WEBHOOK_API_VERSION } from '../webhooks/events.js';
import { IsoDateTime, Uuid } from './common.js';
/**
 * Public API v1 (Phase 2 subset, ADR 0019) and the public payloads sent by webhooks (ADR 0018).
 * Versioned: never remove or rename a field; add optional ones. Owner: architect (S14 implements the routes,
 * each module's webhook `build` produces these shapes).
 */
export const PUBLIC_API_VERSION = WEBHOOK_API_VERSION;
export const ApiPersonRef = z
    .object({
    memberId: Uuid.optional(),
    /** The calling system's own id for the person (matched through external links). */
    externalId: z.string().trim().min(1).max(100).optional(),
    email: z.email().max(254).optional(),
    name: z.string().trim().min(1).max(120).optional(),
})
    .refine((p) => p.memberId || p.externalId || p.email || p.name, { message: 'person needs memberId, externalId, email or name' });
export const ApiSiteRef = z
    .object({ siteId: Uuid.optional(), siteExternalId: z.string().trim().min(1).max(100).optional() })
    .refine((s) => s.siteId || s.siteExternalId, { message: 'siteId or siteExternalId is required' });
const ApiEventBase = {
    occurredAt: IsoDateTime,
    site: ApiSiteRef,
    person: ApiPersonRef,
    /** The calling system's event id (echo protection and support). */
    externalEventId: z.string().trim().max(100).nullable().default(null),
};
/** POST /api/v1/visits/sign-in (header Idempotency-Key required). */
export const ApiSignIn = z.object({ ...ApiEventBase, groupId: Uuid.nullable().default(null) });
/** POST /api/v1/visits/sign-out (header Idempotency-Key required). */
export const ApiSignOut = z.object({ ...ApiEventBase, visitId: Uuid.nullable().default(null) });
export const ApiVisitResult = z.object({
    /** applied | merged (a duplicate or earlier event already covered it) | pending (sign-out waiting for its sign-in) */
    status: z.enum(['applied', 'merged', 'pending']),
    visitId: Uuid.nullable(),
    memberId: Uuid,
});
/** A visit as public payloads and the API show it: no photos, answers, signatures or notes. */
export const PublicVisit = z.object({
    id: Uuid,
    siteId: Uuid,
    siteExternalId: z.string().nullable(),
    groupId: Uuid,
    person: z.object({
        memberId: Uuid.nullable(),
        externalId: z.string().nullable(),
        name: z.string(),
        email: z.string().nullable(),
    }),
    hostMemberId: Uuid.nullable(),
    signedInAt: IsoDateTime,
    signedOutAt: IsoDateTime.nullable(),
    signInMethod: z.string(),
    signOutMethod: z.string().nullable(),
    /** true when the sign-out was automatic (may later be replaced by a real time). */
    autoSignedOut: z.boolean(),
});
/** GET /api/v1/visits/on-site?siteId= */
export const ApiOnSiteQuery = z.object({ siteId: Uuid });
export const ApiOnSite = z.object({ siteId: Uuid, at: IsoDateTime, visits: z.array(PublicVisit) });
export const PublicPreRegistration = z.object({
    id: Uuid,
    siteId: Uuid,
    groupId: Uuid,
    status: z.string(),
    visitorName: z.string(),
    visitorEmail: z.string().nullable(),
    hostMemberId: Uuid.nullable(),
    expectedAt: IsoDateTime,
    expectedUntil: IsoDateTime.nullable(),
    visitId: Uuid.nullable(),
});
export const PublicMember = z.object({
    id: Uuid,
    groupId: Uuid,
    fullName: z.string(),
    email: z.string().nullable(),
    externalId: z.string().nullable(),
    status: z.string(),
});
export const PublicFeedback = z.object({
    id: Uuid,
    visitId: Uuid,
    siteId: Uuid,
    hostMemberId: Uuid.nullable(),
    rating: z.number().int().min(1).max(5),
    hasComment: z.boolean(),
    receivedAt: IsoDateTime,
});
/** The 'minimal' payload profile (OMP link, spec 9.1 rule 6): person id, name, email, site, in/out, times. */
export const MinimalVisitPayload = z.object({
    visitId: Uuid,
    direction: z.enum(['in', 'out']),
    siteId: Uuid,
    siteExternalId: z.string().nullable(),
    person: z.object({ memberId: Uuid.nullable(), externalId: z.string().nullable(), name: z.string(), email: z.string().nullable() }),
    occurredAt: IsoDateTime,
    automatic: z.boolean(),
    /** For a sign-out that replaces an earlier automatic one: the automatic time it replaces. */
    replacesAutomaticAt: IsoDateTime.nullable(),
});
//# sourceMappingURL=public-api.js.map